import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { NgSelectModule } from '@ng-select/ng-select';
import { HttpClient } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ErrorHelperService } from '../../../services/error-helper.service';
import { ParcelsInTripDialogComponent } from '../parcels-in-trip-dialog/parcels-in-trip-dialog.component';
import { CarrierResponse, searchCarrierFn } from '../select-new-tour-dialog/select-new-tour-dialog.component';

export interface TransitWarehouse {
    warehouseCode: string;
    warehouseName: string;
    warehouseShortName: string;
}

export interface ParcelInTrip {
    parcelCode: string;
    parcelWaybillCode: string;
    parcelShipmentCode: string;
    tripCode: string;
    destinationCode: string;
    destinationName: string;
}

export interface ApplyTransitDialogData {
    warehouseCode: string | null;
    comParcelsList: number[];
    transitWarehouses: TransitWarehouse[];
    carriers: CarrierResponse[];
}

interface ParcelsInTripGroup {
    tripCode: string;
    destinationName: string;
    count: number;
}

@Component({
    selector: 'app-apply-transit-dialog',
    standalone: true,
    templateUrl: './apply-transit-dialog.component.html',
    styleUrls: ['../select-new-tour-dialog/select-new-tour-dialog.component.css', './apply-transit-dialog.component.css'],
    imports: [
        CommonModule,
        FormsModule,
        MatDialogModule,
        NgSelectModule
    ]
})
export class ApplyTransitDialogComponent {
    step: 'checking' | 'warning' | 'select' = 'checking';
    transitWarehouseCode: string | null = null;
    error: string | null = null;
    parcelsInTrip: ParcelInTrip[] = [];
    parcelsInTripGroups: ParcelsInTripGroup[] = [];
    loadingWarehouses = false;
    availableTransitWarehouses: TransitWarehouse[] = [];
    carrierCode: string | null = null;
    availableCarriers: CarrierResponse[] = [];

    constructor(
        private http: HttpClient,
        private errorHelperService: ErrorHelperService,
        private dialog: MatDialog,
        private dialogRef: MatDialogRef<ApplyTransitDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ApplyTransitDialogData
    ) {
        this.availableTransitWarehouses = [...(data.transitWarehouses || [])];
        this.availableCarriers = [...(data.carriers || [])];
        this.loadAvailableTransitWarehouses();
        this.checkParcelsInTrip();
    }

    private checkParcelsInTrip(): void {
        const payload = {comParcelsList: this.data.comParcelsList, warehouseCode: this.data.warehouseCode};
        this.http.post<ParcelInTrip[]>(`${environment.apiUrl}/change-tour/apply-transit/check`, payload)
            .subscribe({
                next: (parcelsInTrip) => {
                    this.parcelsInTrip = Array.isArray(parcelsInTrip) ? parcelsInTrip : [];
                    this.parcelsInTripGroups = this.groupParcelsInTrip(this.parcelsInTrip);
                    this.step = this.parcelsInTrip.length === 0 ? 'select' : 'warning';
                },
                error: (err) => {
                    this.step = 'select';
                    this.error = this.errorHelperService.extractBackendMessage(err) || 'Errore nella verifica dei colli';
                }
            });
    }

    private loadAvailableTransitWarehouses(): void {
        this.loadingWarehouses = true;
        const payload = {comParcelsList: this.data.comParcelsList, warehouseCode: this.data.warehouseCode};
        this.http.post<TransitWarehouse[]>(`${environment.apiUrl}/change-tour/transit-warehouses`, payload)
            .pipe(finalize(() => (this.loadingWarehouses = false)))
            .subscribe({
                next: (warehouses) => {
                    this.availableTransitWarehouses = Array.isArray(warehouses) ? warehouses : [];
                    if (!this.availableTransitWarehouses.some(w => w.warehouseCode === this.transitWarehouseCode)) {
                        this.transitWarehouseCode = this.availableTransitWarehouses.length === 1 ? this.availableTransitWarehouses[0].warehouseCode : null;
                    }
                },
                error: (err) => {
                    this.availableTransitWarehouses = [];
                    this.error = this.errorHelperService.extractBackendMessage(err) || 'Errore nel caricamento dei magazzini di transito';
                }
            });
    }

    searchCarrierFn = searchCarrierFn;

    searchWarehouseFn(term: string, item: TransitWarehouse): boolean {
        const t = term.toLowerCase();
        return (
            (item.warehouseName || '').toLowerCase().includes(t) ||
            (item.warehouseShortName || '').toLowerCase().includes(t) ||
            (item.warehouseCode || '').toLowerCase().includes(t)
        );
    }

    continueAfterWarning(): void {
        this.step = 'select';
    }

    openParcelsInTripDialog(): void {
        this.dialog.open(ParcelsInTripDialogComponent, {
            width: '760px',
            maxWidth: '95vw',
            panelClass: 'app-popup-dialog',
            autoFocus: false,
            data: { parcelsInTrip: this.parcelsInTrip }
        });
    }

    cancel(): void {
        this.dialogRef.close(null);
    }

    confirm(): void {
        if (!this.transitWarehouseCode || !this.carrierCode) return;
        this.dialogRef.close({ transitWarehouseCode: this.transitWarehouseCode, carrierCode: this.carrierCode });
    }

    private groupParcelsInTrip(parcelsInTrip: ParcelInTrip[]): ParcelsInTripGroup[] {
        const groups = new Map<string, ParcelsInTripGroup>();
        parcelsInTrip.forEach(parcel => {
            const destinationName = parcel.destinationName || parcel.destinationCode || '-';
            const key = `${parcel.tripCode}|${destinationName}`;
            const group = groups.get(key);
            if (group) {
                group.count++;
            } else {
                groups.set(key, { tripCode: parcel.tripCode, destinationName, count: 1 });
            }
        });
        return Array.from(groups.values());
    }
}
